import { MoolahVaultAbi, MorphoBlueAbi } from '@1delta/abis'
import {
  morphoPools,
  morphoTypeVaults,
  type MorphoTypeVaultEntry,
} from '@1delta/data-sdk'
import lodash from 'lodash'
import { formatEther, parseAbi, parseUnits, zeroAddress, zeroHash } from 'viem'
import { toGenericPriceKey, toOracleKey } from '../../assets'
import {
  decodeMarkets,
  ListaMarket,
} from '../../lending/public-data/morpho/utils/evmParser'
import { MathLib } from '../../lending/public-data/morpho/utils/mathLib'
import { MORPHO_LENS } from '../../lending/user-data/morpho/userCallBuild'
import { GenericTokenList, MulticallRetryFunction } from '../../types'
import { apyToApr, parseRawAmount } from '../../utils/parsing'
import { composeVaultDisplayName } from '../displayName'
import { withIdleExposure } from '../exposure'
import { MorphoVault, MorphoVaults, VaultMarketExposure } from './types'
import {
  MarketRate,
  computeVaultAllocation,
  fetchVaultSupplyShares,
} from './allocationWalk'
import { fetchAssetDecimals } from '../assetDecimals'
import { deriveConvertToAssets } from '../sharePrice'
import { curatorNameFromVaultName } from '../curatorName'
import { dropUnrealizableRates } from './rateSanity'

const { chunk } = lodash

const LISTA_PROTOCOL = 'LISTA_DAO'

/** Same lens + binary record format the Lista markets path uses. */
const LISTA_LENS_ABI = parseAbi([
  'function getListaMarketDataCompact(address morpho, bytes32[] calldata marketsIds) external view returns (bytes memory data)',
])
const LISTA_MARKET_CHUNK = 100

/**
 * Phase-1 view calls — order matters: index → field. `withdrawQueueLength`
 * is appended last (index 8); `parseVault` only consumes the first eight so
 * the extra entry is read separately for the allocation walk.
 */
const PHASE1_CALLS = [
  'name',
  'symbol',
  'decimals',
  'totalAssets',
  'totalSupply',
  'fee',
  'feeRecipient',
  'CURATOR',
  'withdrawQueueLength',
] as const
const PHASE1_PER_VAULT = PHASE1_CALLS.length
const WITHDRAW_QUEUE_LENGTH_INDEX = 8

/** Moolah `fee()` is 1e18-scaled (e.g. `0.05e18` = 5 %). */
const FEE_SCALE = 1e18

const isHex40 = (addr: unknown): addr is string =>
  typeof addr === 'string' && /^0x[0-9a-f]{40}$/i.test(addr)

const lcOrUndefined = (addr: unknown): string | undefined => {
  if (!isHex40(addr)) return undefined
  const lower = (addr as string).toLowerCase()
  return lower === zeroAddress ? undefined : lower
}

const isHex64 = (val: unknown): val is string =>
  typeof val === 'string' && /^0x[0-9a-f]{64}$/i.test(val)

const toNumberSafe = (raw: unknown, fallback = 0): number => {
  if (raw === undefined || raw === null) return fallback
  const n = Number(raw)
  return Number.isFinite(n) ? n : fallback
}

const toStringSafe = (raw: unknown, fallback = ''): string => {
  if (raw === undefined || raw === null) return fallback
  return String(raw)
}

/** Formatted underlying amount → raw integer string (inverse of
 *  `parseRawAmount`). Returns '0' on malformed input. */
const toRawAmount = (formatted: number, decimals: number): string => {
  try {
    return parseUnits(formatted.toFixed(decimals), decimals).toString()
  } catch {
    return '0'
  }
}

const collectEntries = (chainId: string): MorphoTypeVaultEntry[] => {
  const list = morphoTypeVaults()?.[LISTA_PROTOCOL]?.[chainId] ?? []
  const seen = new Set<string>()
  const out: MorphoTypeVaultEntry[] = []
  for (const entry of list) {
    const vaultAddr = entry?.vault?.toLowerCase()
    if (!vaultAddr || seen.has(vaultAddr)) continue
    seen.add(vaultAddr)
    out.push(entry)
  }
  return out
}

export interface FetchListaVaultsFromChainOptions {
  /**
   * Override the registry — `[{ vault, underlying, name? }]`. Lets fork
   * tests point at a specific vault list without populating the global
   * registry.
   */
  vaultsOverride?: MorphoTypeVaultEntry[]
}

/**
 * On-chain Lista DAO (Moolah-fork) vault fetcher. Same output shape as
 * `fetchMorphoVaultsFromChain`, different read path:
 *
 *  - Phase 1 multicall: `name / symbol / decimals / totalAssets /
 *    totalSupply / fee / feeRecipient / CURATOR` per vault. The role hash
 *    returned by `CURATOR()` is contract-defined, not derivable, so it
 *    must be read on-chain.
 *  - Phase 2 multicall: `getRoleMember(CURATOR_ROLE, 0)` per vault →
 *    surfaces the curator address (Moolah uses
 *    `AccessControlEnumerable`, no direct `curator()` getter).
 *
 * `owner` / `guardian` are surfaced as `undefined` (Moolah uses
 * `DEFAULT_ADMIN_ROLE` for admin and has no guardian role; left out to
 * avoid extra round-trips). `timelock` is `0` — no timelock pattern in
 * Moolah.
 *
 * APR is computed via an allocation walk: each vault's `withdrawQueue`
 * enumerates the markets it lends to; `MOOLAH.position(id, vault)` gives
 * the vault's supply shares per market; the Lista lens supplies each
 * market's supply APY + share/asset totals. The vault `depositRate` is the
 * allocation-weighted market supply APR over `totalAssets` (idle assets
 * earn nothing), net of the vault performance `fee`. If the Moolah core
 * address or lens is missing for the chain, APR falls back to `0`.
 */
export const fetchListaVaultsFromChain = async (
  chainId: string,
  multicallRetry: MulticallRetryFunction,
  prices: { [asset: string]: number } = {},
  tokenList: GenericTokenList = {},
  opts: FetchListaVaultsFromChainOptions = {},
): Promise<MorphoVaults> => {
  const entries = opts.vaultsOverride ?? collectEntries(chainId)
  if (entries.length === 0) return {}

  const phase1Calls = entries.flatMap((entry) =>
    PHASE1_CALLS.map((name) => ({
      address: entry.vault,
      name,
      params: [],
    })),
  )

  const phase1 = await multicallRetry({
    chain: chainId,
    calls: phase1Calls as any,
    abi: phase1Calls.map(() => MoolahVaultAbi) as any,
    allowFailure: true,
  })

  const curatorRoleByVault: (string | undefined)[] = []
  const queueLenByVault: number[] = []
  for (let i = 0; i < entries.length; i++) {
    const role = phase1[i * PHASE1_PER_VAULT + 7]
    curatorRoleByVault.push(isHex64(role) ? (role as string) : undefined)
    queueLenByVault.push(
      toNumberSafe(phase1[i * PHASE1_PER_VAULT + WITHDRAW_QUEUE_LENGTH_INDEX]),
    )
  }

  // Phase 2: resolve the curator address (`getRoleMember`) and enumerate
  // each vault's withdraw queue (`withdrawQueue(i)`) in a single round.
  // Calls are flattened; `phase2Meta` maps each result back to its vault.
  type Phase2Meta =
    | { kind: 'curator'; vault: number }
    | { kind: 'queue'; vault: number }
  const phase2Calls: { address: string; name: string; params: any[] }[] = []
  const phase2Meta: Phase2Meta[] = []
  entries.forEach((entry, i) => {
    phase2Calls.push({
      address: entry.vault,
      name: 'getRoleMember',
      params: [curatorRoleByVault[i] ?? zeroHash, 0n],
    })
    phase2Meta.push({ kind: 'curator', vault: i })
    for (let q = 0; q < queueLenByVault[i]; q++) {
      phase2Calls.push({
        address: entry.vault,
        name: 'withdrawQueue',
        params: [BigInt(q)],
      })
      phase2Meta.push({ kind: 'queue', vault: i })
    }
  })

  const phase2 = await multicallRetry({
    chain: chainId,
    calls: phase2Calls as any,
    abi: phase2Calls.map(() => MoolahVaultAbi) as any,
    allowFailure: true,
  })

  const curatorAddrByVault: unknown[] = entries.map(() => undefined)
  const marketIdsByVault: string[][] = entries.map(() => [])
  phase2.forEach((res, k) => {
    const meta = phase2Meta[k]
    if (meta.kind === 'curator') {
      if (curatorRoleByVault[meta.vault]) curatorAddrByVault[meta.vault] = res
    } else if (isHex64(res)) {
      marketIdsByVault[meta.vault].push((res as string).toLowerCase())
    }
  })

  // Phase 3 (parallel): per-market supply rates from the lens, and each
  // vault's supply shares per market from the Moolah core. Both feed the
  // allocation-weighting in `parseVault`.
  const moolah = morphoPools()?.[LISTA_PROTOCOL]?.[chainId]
  const allMarketIds = [...new Set(marketIdsByVault.flat())]
  const underlyings = [
    ...new Set(
      entries
        .map((entry) => entry.underlying?.toLowerCase())
        .filter((a): a is string => !!a),
    ),
  ]
  const [rateMap, positionMap, assetDecimals] = await Promise.all([
    fetchListaMarketRates(chainId, moolah, allMarketIds, multicallRetry),
    fetchVaultSupplyShares(
      chainId,
      moolah,
      entries,
      marketIdsByVault,
      multicallRetry,
    ),
    fetchAssetDecimals(chainId, underlyings, multicallRetry),
  ])

  const out: MorphoVaults = {}
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i]
    const slice = phase1.slice(i * PHASE1_PER_VAULT, (i + 1) * PHASE1_PER_VAULT)
    const parsed = parseVault(
      entry,
      slice,
      curatorAddrByVault[i],
      chainId,
      prices,
      tokenList,
      marketIdsByVault[i],
      rateMap,
      positionMap,
      assetDecimals,
    )
    if (parsed) out[parsed.address] = parsed
  }
  return dropUnrealizableRates(out)
}

/**
 * Decode the Lista lens output for `marketIds` into per-market supply APR
 * and share/asset totals. Mirrors the Lista markets path
 * (`getListaMarketDataConverter`): utilization → Lista IRM borrow APY →
 * supply APY (net of market fee) → APR. Returns an empty map when the lens
 * or Moolah core is unavailable so callers degrade to APR `0`.
 */
async function fetchListaMarketRates(
  chainId: string,
  moolah: string | undefined,
  marketIds: string[],
  multicallRetry: MulticallRetryFunction,
): Promise<Map<string, MarketRate>> {
  const map = new Map<string, MarketRate>()
  const lens = MORPHO_LENS[chainId]
  if (!moolah || !lens || marketIds.length === 0) return map

  const groups = chunk(marketIds, LISTA_MARKET_CHUNK)
  const calls = groups.map((group) => ({
    address: lens,
    name: 'getListaMarketDataCompact',
    params: [moolah, group],
  }))
  const raw = await multicallRetry({
    chain: chainId,
    calls: calls as any,
    abi: calls.map(() => LISTA_LENS_ABI) as any,
    allowFailure: true,
  })

  groups.forEach((group, c) => {
    const bytes = raw[c]
    if (!bytes || bytes === '0x') return
    let decoded: ListaMarket[]
    try {
      decoded = decodeMarkets(bytes) as ListaMarket[]
    } catch {
      return
    }
    decoded.forEach((mkt, j) => {
      const id = group[j]?.toLowerCase()
      if (!id) return
      const utilization = MathLib.getUtilization({
        totalSupplyAssets: mkt.totalSupplyAssets,
        totalBorrowAssets: mkt.totalBorrowAssets,
      })
      let supplyApy = 0n
      try {
        const borrowApy = MathLib.getListaBorrowApy(
          mkt.rateAtTarget,
          utilization,
          undefined,
          Number(mkt.lastUpdate),
          mkt.rateCap,
          mkt.rateFloor,
        )
        supplyApy = MathLib.getSupplyApy(borrowApy, utilization, mkt.fee)
      } catch {}
      const liquidity =
        mkt.totalSupplyAssets > mkt.totalBorrowAssets
          ? mkt.totalSupplyAssets - mkt.totalBorrowAssets
          : 0n
      map.set(id, {
        supplyApr: apyToApr(Number(formatEther(supplyApy))) * 100,
        totalSupplyAssets: mkt.totalSupplyAssets,
        totalSupplyShares: mkt.totalSupplyShares,
        collateralAddress: (mkt.collateralToken ?? '').toLowerCase(),
        liquidity,
      })
    })
  })
  return map
}

function parseVault(
  entry: MorphoTypeVaultEntry,
  results: any[],
  curatorAddrRaw: unknown,
  chainId: string,
  prices: { [asset: string]: number },
  tokenList: GenericTokenList,
  marketIds: string[],
  rateMap: Map<string, MarketRate>,
  positionMap: Map<string, bigint>,
  assetDecimals: Map<string, number>,
): MorphoVault | null {
  const address = entry?.vault?.toLowerCase()
  const underlying = entry?.underlying?.toLowerCase()
  if (!address || !underlying) return null

  const [
    nameRaw,
    symbolRaw,
    decimalsRaw,
    totalAssetsRaw,
    totalSupplyRaw,
    feeRaw,
    /* feeRecipientRaw */
    /* curatorRoleRaw */
    ,
    ,
  ] = results

  const assetMeta = tokenList[underlying]
  // `totalAssets` is in underlying units — format with the asset decimals
  // (read on-chain), not the vault's share `decimals()` (`decimalsRaw`).
  const decimals =
    assetDecimals.get(underlying) ??
    assetMeta?.decimals ??
    Number(decimalsRaw ?? 18)

  const totalAssets = toStringSafe(totalAssetsRaw, '0')
  const totalSupply = toStringSafe(totalSupplyRaw, '0')
  const totalAssetsFormatted = Number(parseRawAmount(totalAssets, decimals))
  // Share price scales by the *share* decimals (`decimalsRaw`, 18 for Moolah),
  // not the asset decimals used for `totalAssets`.
  const convertToAssets = deriveConvertToAssets(
    totalAssets,
    totalSupply,
    Number(decimalsRaw ?? 18),
  )

  const oracleKey =
    toOracleKey(assetMeta?.assetGroup) ?? toGenericPriceKey(underlying, chainId)
  const priceUsd = prices[oracleKey] ?? prices[underlying] ?? 0

  const onchainName = toStringSafe(nameRaw).trim()
  const fallbackName = onchainName || (entry.name ?? '').trim()
  const symbol = toStringSafe(symbolRaw)

  const fee = (toNumberSafe(feeRaw) / FEE_SCALE) * 100

  const { depositRate, exposures, liquidityFormatted } = computeVaultAllocation(
    address,
    decimals,
    totalAssetsFormatted,
    fee,
    priceUsd,
    marketIds,
    rateMap,
    positionMap,
    tokenList,
    { chainId, underlying, protocol: 'LISTA_DAO' },
  )

  return {
    address,
    underlying,
    symbol,
    name: fallbackName,
    displayName: composeVaultDisplayName(
      'Lista',
      undefined,
      assetMeta,
      fallbackName,
    ),
    decimals,
    totalAssets,
    totalSupply,
    convertToAssets,
    supplyRate: depositRate,
    rewardsRate: 0,
    depositRate,
    fee,
    timelock: 0,
    whitelisted: true,
    owner: undefined,
    curator: lcOrUndefined(curatorAddrRaw),
    // Moolah exposes only the curator address; derive a display name from the
    // (curator-branded) vault name as a best-effort fallback.
    curatorName: curatorNameFromVaultName(fallbackName, assetMeta?.symbol),
    guardian: undefined,
    asset: assetMeta,
    priceUsd: priceUsd || undefined,
    totalAssetsFormatted,
    totalAssetsUsd: totalAssetsFormatted * priceUsd,
    // Real withdrawable: idle + Σ min(allocation, market liquidity) across the
    // vault's Moolah markets (see `computeVaultAllocation`). Fully-liquid case
    // reuses the exact raw total to avoid float round-trip drift.
    liquidity:
      liquidityFormatted >= totalAssetsFormatted
        ? totalAssets
        : toRawAmount(liquidityFormatted, decimals),
    liquidityFormatted,
    liquidityUsd: liquidityFormatted * priceUsd,
    exposures: exposures.length > 0 ? exposures : undefined,
  }
}
